import React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { styled } from '@mui/system';
import moment from 'moment';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MultipleIcon from '../../NRIcons/MultipleIcon';
import NRRelativeTime from '../NRRelativeTime/NRRelativeTime';
import NRSelect from '../NRControlledSelect/NRSelect';
import NRActivityFeedItemHeader from './NRActivityFeedItemHeader';
import NRActivityFeedIcon from './NRActivityFeedIcon';
import NRExportToCsvDialog from '../NRExportToCsvDialog/NRExportToCsvDialog';
import NRExportIcon from '../../NRIcons/NRExportIcon';
import { useTheme } from '@mui/material/styles';
import NRActivityFeedVirtualizedList from '../NRVirtualizedLists/NRActivityFeedVirtualizedList';
import { useFlag } from '@openfeature/react-sdk';
import { FEATURE_FLAGS } from '../../constants/constants';

let ActivityEntityOptions = [
  { value: 'ALL', label: 'All' },
  { value: 'ASSET', label: 'Asset' },
  { value: 'ORGANIZATION', label: 'Organization' },
  { value: 'LICENSE_ISSUE', label: 'License Issues' },
  { value: 'MISCONFIGURATION', label: 'Misconfigurations' },
  { value: 'IDENTIFICATION', label: 'SBOM' },
  { value: 'SECRET', label: 'Secrets' },
  { value: 'VULNERABILITY', label: 'Vulnerabilities' }
];

const AltAvatar = styled(props => <Avatar variant="circular" {...props} />)(({ theme }) => ({
  backgroundColor: theme.palette.background.default,
  color: theme.palette.text.iconInfo,
  width: theme.spacing(4.5),
  height: theme.spacing(4.5)
}));

const ValueContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  marginLeft: theme.spacing(2),
  minWidth: theme.spacing(7.5)
}));

const ActivityFeedQuickSearch = React.memo(function ActivityFeedQuickSearch({
  searchInput,
  setSearchInput,
  selectedEntity,
  setSelectedEntity,
  total,
  isLoading,
  setExportToCsvDialogIsOpen
}) {
  const handleOnKeyDown = event => {
    if (event.keyCode === 13 && !event.shiftKey && searchInput?.length >= 3) {
      event.preventDefault();
    }
  };

  const { value: isSecretsEnable } = useFlag(FEATURE_FLAGS.SECRETS_DETECTION, false);
  if (!isSecretsEnable) {
    ActivityEntityOptions = ActivityEntityOptions.filter(option => option.value !== 'SECRETS');
  }

  return (
    <Box sx={{ padding: 1, display: 'flex', flexGrow: 1 }}>
      <TextField
        onKeyDown={handleOnKeyDown}
        multiline={false}
        sx={{
          width: '60%',
          marginRight: 2
        }}
        id="activity-feed-filters"
        placeholder={'Quick Search'}
        color={'secondary'}
        value={searchInput}
        onChange={e => setSearchInput(e.target.value)}
        label={
          <Typography display={'flex'}>
            <SearchIcon />
            Quick Search
          </Typography>
        }
        InputProps={
          !!searchInput
            ? {
                endAdornment: (
                  <Tooltip title="Clear">
                    <IconButton size="small" aria-label="delete" onClick={() => setSearchInput('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )
              }
            : null
        }
        variant="outlined"
        size="small"
      />
      <NRSelect
        sxProps={{ width: '30%' }}
        size={'small'}
        variant={'outlined'}
        id={`activity-entity-selector`}
        value={selectedEntity}
        showEmptyValue={false}
        onChange={setSelectedEntity}
        options={ActivityEntityOptions}
        disabled={isLoading}
        label={'View'}
      />
      <ValueContainer>
        {isLoading ? (
          <CircularProgress size={20} />
        ) : (
          <Box display="flex" alignItems="center" tabIndex={0} gap={0.5} aria-label={`Total activities: ${total}`}>
            <Typography variant="label" children={`${total}`} />
            <Typography variant="label" color="text.secondary" children={'total'} />
          </Box>
        )}
      </ValueContainer>
      <Tooltip title="Export to CSV">
        <IconButton
          onClick={() => setExportToCsvDialogIsOpen(true)}
          color="secondary"
          size="small"
          sx={{
            padding: 0.75,
            marginLeft: 1
          }}
        >
          <NRExportIcon />
        </IconButton>
      </Tooltip>
    </Box>
  );
});

/** Timeline rail: each row draws only its own segment(s). Adjacent rows meet at the shared edge — no need to know the next item's height. */
function getTimelineConnectorSx({ theme, left, avatarCenter, avatarRadius, showIncomingLine, showOutgoingLine }) {
  if (!showIncomingLine && !showOutgoingLine) return {};

  const lineSx = {
    content: '""',
    position: 'absolute',
    left,
    borderLeft: `1px solid ${theme.palette.divider}`,
    zIndex: 0
  };

  if (showIncomingLine && showOutgoingLine) {
    // Full height; avatar background covers the middle
    Object.assign(lineSx, { top: 0, bottom: 0 });
  } else if (showOutgoingLine) {
    Object.assign(lineSx, { top: `calc(${avatarCenter} + ${avatarRadius})`, bottom: 0 });
  } else {
    Object.assign(lineSx, { top: 0, height: `calc(${avatarCenter} - ${avatarRadius})` });
  }

  return { '&::before': lineSx };
}

function ActivityFeedItem({ item, showTime = true, handleClose, showIncomingLine = false, showOutgoingLine = false }) {
  const theme = useTheme();
  const createdAtAsDate = new Date(item.createdAt);
  const avatarSize = theme.spacing(4.5); // matches AltAvatar
  const avatarRadius = `calc(${avatarSize} / 2)`;
  // time column (104) + gap (spacing 2) + half avatar — vertical center of the icon
  const connectorLeft = `calc(104px + ${theme.spacing(2)} + ${avatarRadius})`;
  // With py + alignItems: center, the avatar center is the row midpoint
  const avatarCenter = '50%';

  return (
    <Box
      key={item.id}
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 1.5,
        ...(!showTime ? { maxWidth: 398 } : {}),
        position: 'relative',
        ...(showTime ? getTimelineConnectorSx({ theme, left: connectorLeft, avatarCenter, avatarRadius, showIncomingLine, showOutgoingLine }) : {})
      }}
    >
      {showTime && <NRRelativeTime sx={{ width: 104, marginRight: 2, textAlign: 'right', flexShrink: 0 }} variant="body2" value={createdAtAsDate} />}
      <AltAvatar aria-hidden="true" sx={{ position: 'relative', zIndex: 1 }}>
        <NRActivityFeedIcon item={item} />
      </AltAvatar>
      <Box
        sx={{
          marginLeft: 2,
          maxWidth: 430, // in order for the ellipsis to work
          overflow: 'hidden',
          width: '100%',
          minHeight: 44,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          justifyContent: 'center'
        }}
      >
        <NRActivityFeedItemHeader item={item} handleClose={handleClose} />
      </Box>
    </Box>
  );
}

function ActivityFeedGroupItem({ items, handleClose, showIncomingLine = false, showOutgoingLine = false }) {
  const theme = useTheme();
  const createdAtAsDate = new Date(items[0].createdAt);
  const mainItem = items[0];
  const [expanded, setExpanded] = useState(false);
  const handleExpandedChange = useCallback((_event, isExpanded) => {
    setExpanded(isExpanded);
  }, []);
  const avatarSize = theme.spacing(4.5);
  const avatarRadius = `calc(${avatarSize} / 2)`;
  const connectorLeft = `calc(104px + ${theme.spacing(2)} + ${avatarRadius})`;
  // Group avatar is top-aligned: py (1.5) + inner pt (0.5) + radius
  const avatarCenter = `calc(${theme.spacing(2)} + ${avatarRadius})`;
  // When expanded, only keep the incoming segment — details has its own internal line
  const outgoing = showOutgoingLine && !expanded;

  return (
    <Box
      key={mainItem.id}
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        py: 1.5,
        position: 'relative',
        ...getTimelineConnectorSx({
          theme,
          left: connectorLeft,
          avatarCenter,
          avatarRadius,
          showIncomingLine,
          showOutgoingLine: outgoing
        })
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'start', pt: 0.5 }} tabIndex={0}>
        <NRRelativeTime
          sx={{ width: 104, marginRight: 2, marginTop: '8px', textAlign: 'right', flexShrink: 0 }}
          variant="body2"
          value={createdAtAsDate}
        />
        <AltAvatar aria-hidden="true" sx={{ position: 'relative', zIndex: 1 }}>
          <MultipleIcon />
        </AltAvatar>
      </Box>
      <Accordion
        slotProps={{ transition: { unmountOnExit: true } }}
        expanded={expanded}
        onChange={handleExpandedChange}
        sx={{
          minHeight: '36px',
          '&.Mui-expanded': {
            minHeight: '36px',
            marginTop: 0
          },
          '&::before': {
            display: 'none'
          }
        }}
        key={`panel${mainItem.id}-${mainItem.dayLabel}`}
        elevation={0}
      >
        <AccordionSummary
          sx={{
            justifyContent: 'start',
            minHeight: '36px',
            '&.Mui-expanded': {
              minHeight: '36px'
            },
            '.MuiAccordionSummary-content': {
              flexGrow: 'unset',
              margin: '0px'
            }
          }}
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`panel${mainItem.id}-header`}
          id={`panel${mainItem.id}-${mainItem.dayLabel}`}
        >
          <Typography component="span" sx={{ height: 44, display: 'flex', alignItems: 'center' }}>{`${items.length} updates`}</Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            maxWidth: 430, // in order for the ellipsis to work
            overflow: 'hidden',
            pr: 0,
            // the line to the left of the accordion
            '&::before': {
              content: '""',
              position: 'absolute',
              left: -18,
              top: 40,
              height: 'calc(100% - 28px)',
              borderLeft: `1px solid ${theme.palette.divider}`
            }
          }}
        >
          {!!items?.length && (
            <NRActivityFeedVirtualizedList
              list={items}
              Component={({ index, style }) => (
                <Box style={style}>
                  <ActivityFeedItem key={items[index].id} item={items[index]} handleClose={handleClose} showTime={false} />
                </Box>
              )}
              listHeight={600}
            />
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
}

const ActivityFeedItems = React.memo(function ActivityFeedItems({ items, handleClose }) {
  return (
    <Box sx={{}}>
      {items.map(({ dayLabel, timeGroups }, idx) => (
        <Accordion
          slotProps={{ transition: { unmountOnExit: true } }}
          sx={{
            '&::before': {
              display: 'none'
            }
          }}
          key={`panel${idx}-${dayLabel}`}
          elevation={0}
          defaultExpanded={idx === 0}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`panel${idx}-header`} id={`panel${idx}-${dayLabel}`}>
            <Typography variant="subtitle2">{dayLabel}</Typography>
          </AccordionSummary>
          <AccordionDetails sx={{}}>
            {timeGroups.map((timeGroup, idx, arr) =>
              timeGroup.length === 1 ? (
                <ActivityFeedItem
                  handleClose={handleClose}
                  key={timeGroup[0].id}
                  item={timeGroup[0]}
                  showIncomingLine={idx > 0}
                  showOutgoingLine={idx < arr.length - 1}
                />
              ) : (
                <ActivityFeedGroupItem
                  handleClose={handleClose}
                  key={`group-${timeGroup[0].id}`}
                  items={timeGroup}
                  showIncomingLine={idx > 0}
                  showOutgoingLine={idx < arr.length - 1}
                />
              )
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
});

const matchesSearch = (input, ...fields) => {
  const search = input.toLowerCase();
  return fields.some(f => f?.toLowerCase().includes(search));
};

function ActivityFeedContent({ activity, isLoading, handleClose, exportToCsvConfig }) {
  const [searchInput, setSearchInput] = useState('');
  const [selectedEntity, setSelectedEntity] = useState(ActivityEntityOptions[0].value);
  const [exportToCsvDialogIsOpen, setExportToCsvDialogIsOpen] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [itemsToDisplay, setItemsToDisplay] = useState([]);
  const [filteredCount, setFilteredCount] = useState(0);

  const enhancedActivity = useMemo(
    () =>
      activity.map(item => ({
        ...item,
        activityDate: getDayLabel(item.createdAt)
      })),
    [activity]
  );

  // Defer heavy computation to avoid blocking UI
  useEffect(() => {
    setIsProcessing(true);

    // Use setTimeout to allow spinner to render
    const timer = setTimeout(() => {
      const lowerCaseSearchInput = searchInput.toLowerCase();

      const filtered = enhancedActivity.filter(item => {
        const isEntityTypeMatch = selectedEntity === ActivityEntityOptions[0].value || item.entityType === selectedEntity;

        if (!isEntityTypeMatch) return false;
        if (!searchInput) return true;

        return matchesSearch(
          lowerCaseSearchInput,
          item.activityType,
          item.user,
          item.description,
          item.entity?.cve,
          item.entity?.checkName,
          item.entity?.name,
          item.entity?.componentName,
          item.entity?.userName,
          item.entity?.hash,
          item.entity?.checkId,
          item.entity?.groupId
        );
      });

      setFilteredCount(filtered.length);
      const grouped = groupActivitiesForDisplay(filtered);
      setItemsToDisplay(grouped);
      setIsProcessing(false);
    }, 0);

    return () => clearTimeout(timer);
  }, [enhancedActivity, searchInput, selectedEntity]);

  const handleSearchChange = useCallback(value => {
    setSearchInput(value);
  }, []);

  const handleEntityChange = useCallback(value => {
    setSelectedEntity(value);
  }, []);

  const showLoading = isLoading || isProcessing;

  return (
    <Box sx={{ padding: 2 }}>
      <ActivityFeedQuickSearch
        searchInput={searchInput}
        setSearchInput={handleSearchChange}
        total={filteredCount}
        selectedEntity={selectedEntity}
        setSelectedEntity={handleEntityChange}
        isLoading={showLoading}
        setExportToCsvDialogIsOpen={setExportToCsvDialogIsOpen}
      />
      <ActivityFeedItems handleClose={handleClose} items={itemsToDisplay} />
      <NRExportToCsvDialog
        isOpen={exportToCsvDialogIsOpen}
        setIsOpen={setExportToCsvDialogIsOpen}
        selectAllFetchConfig={exportToCsvConfig}
        customCSVExportRouteName={'activities'}
        totalCount={activity?.length}
      />
    </Box>
  );
}
export default ActivityFeedContent;

export function getDayLabel(dateString) {
  const date = moment(dateString).startOf('day');
  const today = moment().startOf('day');
  const yesterday = moment().subtract(1, 'day').startOf('day');

  if (date.isSame(today, 'day')) {
    return 'Today';
  } else if (date.isSame(yesterday, 'day')) {
    return 'Yesterday';
  }

  const daysAgo = today.diff(date, 'days');

  if (daysAgo >= 2 && daysAgo <= 7) {
    return date.format('dddd'); // e.g., "Monday"
  } else if (date.isSame(today, 'year')) {
    return date.format('MMMM D'); // e.g., "August 10"
  } else {
    return date.format('MMMM D, YYYY'); // e.g., "August 10, 2022"
  }
}

function groupByTimeWindow(activities, thresholdMinutes = 5) {
  const sorted = [...activities].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const { groups, currentGroup } = sorted.reduce(
    (acc, activity) => {
      const { groups, currentGroup } = acc;

      if (currentGroup.length === 0) {
        currentGroup.push(activity);
        return acc;
      }

      const firstInGroup = currentGroup[0];
      const diff = moment(firstInGroup.createdAt).diff(activity.createdAt, 'minutes');

      if (diff <= thresholdMinutes) {
        currentGroup.push(activity);
      } else {
        groups.push([...currentGroup]);
        acc.currentGroup = [activity]; // reset current group
      }

      return acc;
    },
    { groups: [], currentGroup: [] }
  );

  if (currentGroup.length) {
    groups.push(currentGroup);
  }

  return groups;
}

export function groupActivitiesForDisplay(activityList) {
  const groupedByDay = {};

  for (const activity of activityList) {
    const label = getDayLabel(activity.createdAt);
    if (!groupedByDay[label]) groupedByDay[label] = [];
    groupedByDay[label].push(activity);
  }

  // Sort days by date (newest first) before grouping into time clusters
  return Object.entries(groupedByDay)
    .sort(([, activitiesA], [, activitiesB]) => {
      // Sort by the most recent activity in each day group
      return new Date(activitiesB[0].createdAt) - new Date(activitiesA[0].createdAt);
    })
    .map(([dayLabel, activities]) => ({
      dayLabel,
      timeGroups: groupByTimeWindow(activities)
    }));
}
